import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';

const VariantPricingModal = ({ isOpen, onClose, product, variants, onSuccess }) => {
  const [pricingData, setPricingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && variants) {
      // Group variants by RAM/ROM for new item prices
      const grouped = {};
      
      variants.forEach(variant => {
        const key = `${variant.ram || 'N/A'}-${variant.storage}`;
        if (!grouped[key]) {
          grouped[key] = {
            storage: variant.storage,
            ram: variant.ram,
            count: 0,
            mrp: variant.mrp || '',
            fsp: variant.fsp || '',
            hasPrice: !!(variant.mrp && variant.fsp)
          };
        }
        grouped[key].count++;
      });

      const combos = Object.values(grouped);
      setPricingData(combos);
    }
  }, [isOpen, variants, product]);

  const handlePriceChange = (index, field, value) => {
    const updated = [...pricingData];
    updated[index][field] = value;
    setPricingData(updated);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const incomplete = pricingData.filter(item => !item.mrp || !item.fsp);
      if (incomplete.length > 0) {
        setError(`Please enter prices for all ${incomplete.length} combination(s)`);
        setLoading(false);
        return;
      }

      const updates = pricingData.map(item => ({
        storage: item.storage,
        ram: item.ram,
        mrp: parseInt(item.mrp),
        fsp: parseInt(item.fsp)
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/variants/pricing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ pricing_updates: updates })
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`✓ Pricing updated successfully!\n${data.updated_count} variants updated.`);
        onSuccess();
      } else {
        setError(data.detail || 'Failed to update pricing');
      }
    } catch (err) {
      console.error('Error updating pricing:', err);
      setError('Failed to update pricing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-[#2b3380] px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Edit New Item Price - {product?.model_name}
            </h3>
            <button onClick={onClose} className="text-white hover:text-white/80">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[60vh] overflow-y-auto">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Enter MRP and FSP for each RAM/ROM combination. Prices apply to all colors automatically.
              </p>
            </div>

            <div className="space-y-4">
              {pricingData.map((combo, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 ${
                    combo.hasPrice ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {combo.ram || 'N/A'} / {combo.storage}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {combo.count} color{combo.count > 1 ? 's' : ''}
                      </p>
                    </div>
                    {combo.hasPrice && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Set
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">MRP (₹)</label>
                      <input
                        type="number"
                        value={combo.mrp}
                        onChange={(e) => handlePriceChange(index, 'mrp', e.target.value)}
                        placeholder="e.g., 119900"
                        className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">FSP (₹)</label>
                      <input
                        type="number"
                        value={combo.fsp}
                        onChange={(e) => handlePriceChange(index, 'fsp', e.target.value)}
                        placeholder="e.g., 89999"
                        className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-md hover:bg-[#1f2660] disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Prices'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantPricingModal;
